import { call, put, takeLatest } from 'redux-saga/effects';
import {
  FETCH_AGENCY_LIST,
  FETCH_AGENCY_LIST_REQUEST,
  FETCH_AGENCY_LIST_SUCCESS,
  FETCH_AGENCY_LIST_FAILURE,
  FETCH_ORDERS,
  FETCH_ORDERS_REQUEST,
  FETCH_ORDERS_SUCCESS,
  FETCH_ORDERS_FAILURE,
  FETCH_ACCOUNT_DETAILS,
  FETCH_ACCOUNT_DETAILS_REQUEST,
  FETCH_ACCOUNT_DETAILS_SUCCESS,
  FETCH_ACCOUNT_DETAILS_FAILURE,
  DOWNLOAD_AGENCY_TEMPLATE,
  DOWNLOAD_AGENCY_TEMPLATE_REQUEST,
  DOWNLOAD_AGENCY_TEMPLATE_SUCCESS,
  DOWNLOAD_AGENCY_TEMPLATE_FAILURE,
  UPLOAD_AGENCY_FUNDS,
  UPLOAD_AGENCY_FUNDS_REQUEST,
  UPLOAD_AGENCY_FUNDS_SUCCESS,
  UPLOAD_AGENCY_FUNDS_FAILURE,
} from './actions';
import * as superAdminAPI from '../../../api/superAdmin';
import errorStrings from '../../../constants/errorStrings';
import { downloadFile, createExcelBlob } from '../../../utils/fileDownloadUtil';

// Agency List Saga
export function* fetchAgencyList() {
  try {
    yield put({
      type: FETCH_AGENCY_LIST_REQUEST,
    });
    
    const { response, error } = yield call(superAdminAPI.fetchAgencyList);
    
    if (!!response) {
      yield put({
        type: FETCH_AGENCY_LIST_SUCCESS,
        payload: {
          data: response.data
        },
      });
    } else {
      yield put(
        onFetchAgencyListError({
          error: error?.data.message ? error?.data.message : errorStrings.commonError,
        }),
      );
    }
  } catch (error) {
    yield put(
      onFetchAgencyListError({
        error: errorStrings.commonError,
      }),
    );
  }
}

// Orders Saga
export function* fetchOrders({ payload }) {
  try {
    yield put({
      type: FETCH_ORDERS_REQUEST,
    });
    
    const { response: rawResponse, error } = yield call(superAdminAPI.fetchOrders, payload);
    let response;
    if(typeof rawResponse === 'string') {
      const validJsonString = rawResponse.replace(/\bNaN\b/g, 'null');
      const responseParse = JSON.parse(validJsonString);
      response = responseParse;
    } else {
      response = rawResponse;
    }
    
    if (!!response && response.data) {
      const ordersData = Array.isArray(response.data.data) ? response.data.data : [];
      const paginationData = {
        current_page: response.data.current_page || 1,
        total_pages: response.data.total_pages || 1,
        total_records: response.data.total_records || 0,
        page_size: payload.page_size || 20
      };
      
      yield put({
        type: FETCH_ORDERS_SUCCESS,
        payload: {
          data: {
            orders: ordersData,
            ...paginationData
          }
        },
      });
    } else {
      yield put(
        onFetchOrdersError({
          error: error?.data.message ? error?.data.message : errorStrings.commonError,
        }),
      );
    }
  } catch (error) {
    yield put(
      onFetchOrdersError({
        error: errorStrings.commonError,
      }),
    );
  }
}

// Account Details Saga
export function* fetchAccountDetails() {
  try {
    yield put({
      type: FETCH_ACCOUNT_DETAILS_REQUEST,
    });
    
    const { response: rawResponse, error } = yield call(superAdminAPI.fetchAccountDetails);
    let response;
    if(typeof rawResponse === 'string') {
      const validJsonString = rawResponse.replace(/\bNaN\b/g, 'null');
      const responseParse = JSON.parse(validJsonString);
      response = responseParse.data;
    } else {
      response = rawResponse;
    }
    
    if (!!response && response.data) {
      const accountsData = Array.isArray(response.data.data) ? response.data.data : [];
      yield put({
        type: FETCH_ACCOUNT_DETAILS_SUCCESS,
        payload: {
          data: {
            accounts: accountsData,
          }
        },
      });
    } else {
      yield put(
        onFetchAccountDetailsError({
          error: error?.data.message ? error?.data.message : errorStrings.commonError,
        }),
      );
    }
  } catch (error) {
    yield put(
      onFetchAccountDetailsError({
        error: errorStrings.commonError,
      }),
    );
  }
}

// Download Agency Template Saga
export function* downloadAgencyTemplate() {
  try {
    yield put({ type: DOWNLOAD_AGENCY_TEMPLATE_REQUEST });
    
    const { response, error } = yield call(superAdminAPI.downloadAgencyTemplate);
    
    if (response && !error) {
      // Create blob and trigger download
      const blob = createExcelBlob(response);
      downloadFile(blob, 'agency_template.xlsx');
      
      yield put({
        type: DOWNLOAD_AGENCY_TEMPLATE_SUCCESS,
      });
    } else {
      yield put(
        onDownloadTemplateError({
          error: error?.data?.message || errorStrings.commonError,
        }),
      );
    }
  } catch (error) {
    yield put(
      onDownloadTemplateError({
        error: errorStrings.commonError,
      }),
    );
  }
}

// Upload Agency Funds Saga
export function* uploadAgencyFunds({ payload }) {
  try {
    yield put({ type: UPLOAD_AGENCY_FUNDS_REQUEST });
    
    const { response, error } = yield call(superAdminAPI.uploadAgencyFunds, { file: payload });
    
    if (response && !error) {
      yield put({
        type: UPLOAD_AGENCY_FUNDS_SUCCESS,
        payload: {
          data: response,
        },
      });
    } else {
      yield put(
        onUploadAgencyFundsError({
          error: error?.data?.message || errorStrings.commonError,
        }),
      );
    }
  } catch (error) {
    yield put(
      onUploadAgencyFundsError({
        error: errorStrings.commonError,
      }),
    );
  }
}

// Error handlers
const onFetchAgencyListError = (payload = {}) => ({
  type: FETCH_AGENCY_LIST_FAILURE,
  payload,
});

const onFetchOrdersError = (payload = {}) => ({
  type: FETCH_ORDERS_FAILURE,
  payload,
});

const onFetchAccountDetailsError = (payload = {}) => ({
  type: FETCH_ACCOUNT_DETAILS_FAILURE,
  payload,
});

const onDownloadTemplateError = (payload = {}) => ({
  type: DOWNLOAD_AGENCY_TEMPLATE_FAILURE,
  payload,
});

const onUploadAgencyFundsError = (payload = {}) => ({
  type: UPLOAD_AGENCY_FUNDS_FAILURE,
  payload,
});

// Watcher functions
function* watchFetchAgencyList() {
  yield takeLatest(FETCH_AGENCY_LIST, fetchAgencyList);
}

function* watchFetchOrders() {
  yield takeLatest(FETCH_ORDERS, fetchOrders);
}

function* watchFetchAccountDetails() {
  yield takeLatest(FETCH_ACCOUNT_DETAILS, fetchAccountDetails);
}

function* watchDownloadAgencyTemplate() {
  yield takeLatest(DOWNLOAD_AGENCY_TEMPLATE, downloadAgencyTemplate);
}

function* watchUploadAgencyFunds() {
  yield takeLatest(UPLOAD_AGENCY_FUNDS, uploadAgencyFunds);
}

export const sagas = [
  watchFetchAgencyList,
  watchFetchOrders,
  watchFetchAccountDetails,
  watchDownloadAgencyTemplate,
  watchUploadAgencyFunds
];
